/**
 * ActionHandlers - Bridges XState actions to game systems
 * Part of Sprint 0023: Unified State Observability System
 * 
 * This module implements the actual game logic that XState actions trigger.
 * Each handler receives the actor context and can interact with:
 * - AnimationController (for playing animations)
 * - CombatSystem (for weapon attachment, hitboxes)
 * - Character physics (for movement forces)
 */

import type { AnimationController } from '../systems/AnimationController';
import type { Character } from '../systems/Character';
import { logger } from '../core/Logger';
import { StateManager } from './StateManager';

// ============================================================================
// Handler Registry Type
// ============================================================================

export interface CharacterActionHandlers {
  // Locomotion animations
  playIdleAnimation: (characterId: string) => void;
  playWalkAnimation: (characterId: string, direction: number) => void;
  playJumpAnimation: (characterId: string) => void;
  playFallAnimation: (characterId: string) => void;
  playLandAnimation: (characterId: string) => void;
  playTurnAnimation: (characterId: string, direction: number) => void;
  
  // Combat actions
  attachWeapon: (characterId: string, attackId: string) => void;
  detachWeapon: (characterId: string) => void;
  enableHitbox: (characterId: string, attackId: string) => void;
  disableHitbox: (characterId: string) => void;
  playAttackAnimation: (characterId: string, attackId: string, phase: 'windup' | 'active' | 'recovery') => void;
  
  // Health actions
  triggerDeath: (characterId: string) => void;
  triggerRespawn: (characterId: string) => void;
  
  // Path constraint physics (Phase 3)
  onPathConstraintEnabled?: (characterId: string) => void;
  onPathConstraintDisabled?: (characterId: string) => void;

  // Physics collision hooks (Sprint 0024)
  onCollisionStart?: (characterId: string, otherBodyId?: string, pairKey?: string) => void;
  onCollisionEnd?: (characterId: string, otherBodyId?: string, pairKey?: string) => void;
}

export interface PhysicsHooks {
  onPhysicsStep?: (deltaTime: number) => void;
  onCollisionStart?: (bodyAId?: string, bodyBId?: string, pairKey?: string) => void;
  onCollisionEnd?: (bodyAId?: string, bodyBId?: string, pairKey?: string) => void;
}

export interface DynamicObjectPhysicsHandler {
  onObjectSleep?: (objectId: string, behaviorId?: string) => void;
  onObjectWake?: (objectId: string, behaviorId?: string) => void;
  onObjectCollision?: (objectId: string, behaviorId?: string, otherBodyId?: string, pairKey?: string) => void;
  onObjectBreak?: (objectId: string, behaviorId?: string) => void;
}

// ============================================================================
// Action Handler Registry
// ============================================================================

/**
 * ActionHandlerRegistry - Connects XState machine actions to game systems
 * 
 * Usage:
 * 1. Game systems register their handlers on init
 * 2. XState actions call registry methods
 * 3. Registry routes to appropriate handler
 */
class ActionHandlerRegistryImpl {
  private animationControllers: Map<string, AnimationController> = new Map();
  private characters: Map<string, Character> = new Map();
  
  // Custom handlers that can be registered by game code
  private customHandlers: Partial<CharacterActionHandlers> = {};

  // Physics hooks (global)
  private physicsHooks: Partial<PhysicsHooks> = {};

  // Dynamic object lifecycle hooks
  private dynamicObjectHandlers: Partial<DynamicObjectPhysicsHandler> = {};
  
  // ============================================================================
  // Registration
  // ============================================================================
  
  registerAnimationController(characterId: string, controller: AnimationController): void {
    this.animationControllers.set(characterId, controller);
    logger.core.log(`[ActionHandlers] Registered AnimationController for ${characterId}`);
  }
  
  unregisterAnimationController(characterId: string): void {
    this.animationControllers.delete(characterId);
  }
  
  registerCharacter(characterId: string, character: Character): void {
    this.characters.set(characterId, character);
    logger.core.log(`[ActionHandlers] Registered Character for ${characterId}`);
  }
  
  unregisterCharacter(characterId: string): void {
    this.characters.delete(characterId);
  }
  
  /**
   * Register custom action handlers.
   * This allows game code to override or extend default behavior.
   */
  registerCustomHandlers(handlers: Partial<CharacterActionHandlers>): void {
    this.customHandlers = { ...this.customHandlers, ...handlers };
  }

  /**
   * Register global physics hooks.
   * Used by physics systems to notify game logic without hard dependencies.
   */
  registerPhysicsHooks(hooks: Partial<PhysicsHooks>): void {
    this.physicsHooks = { ...this.physicsHooks, ...hooks };
  }

  registerDynamicObjectHandlers(handlers: Partial<DynamicObjectPhysicsHandler>): void {
    this.dynamicObjectHandlers = { ...this.dynamicObjectHandlers, ...handlers };
  }

  onPhysicsStep(deltaTime: number): void {
    this.physicsHooks.onPhysicsStep?.(deltaTime);
  }

  onGlobalCollisionStart(bodyAId?: string, bodyBId?: string, pairKey?: string): void {
    this.physicsHooks.onCollisionStart?.(bodyAId, bodyBId, pairKey);
  }

  onGlobalCollisionEnd(bodyAId?: string, bodyBId?: string, pairKey?: string): void {
    this.physicsHooks.onCollisionEnd?.(bodyAId, bodyBId, pairKey);
  }

  onObjectCollision(objectId: string, behaviorId?: string, otherBodyId?: string, pairKey?: string): void {
    this.dynamicObjectHandlers.onObjectCollision?.(objectId, behaviorId, otherBodyId, pairKey);
  }

  onObjectBreak(objectId: string, behaviorId?: string): void {
    this.dynamicObjectHandlers.onObjectBreak?.(objectId, behaviorId);
  }

  onObjectSleep(objectId: string, behaviorId?: string): void {
    this.dynamicObjectHandlers.onObjectSleep?.(objectId, behaviorId);
  }

  onObjectWake(objectId: string, behaviorId?: string): void {
    this.dynamicObjectHandlers.onObjectWake?.(objectId, behaviorId);
  }
  
  // ============================================================================
  // Locomotion Actions
  // ============================================================================
  
  playIdleAnimation(characterId: string): void {
    if (this.customHandlers.playIdleAnimation) {
      this.customHandlers.playIdleAnimation(characterId);
      return;
    }
    
    const controller = this.animationControllers.get(characterId);
    if (controller) {
      controller.setState('idle', 0.2);
    }
  }

  onCollisionStart(characterId: string, otherBodyId?: string, pairKey?: string): void {
    this.customHandlers.onCollisionStart?.(characterId, otherBodyId, pairKey);
  }

  onCollisionEnd(characterId: string, otherBodyId?: string, pairKey?: string): void {
    this.customHandlers.onCollisionEnd?.(characterId, otherBodyId, pairKey);
  }
  
  playWalkAnimation(characterId: string, _direction: number): void {
    if (this.customHandlers.playWalkAnimation) {
      this.customHandlers.playWalkAnimation(characterId, _direction);
      return;
    }
    
    const controller = this.animationControllers.get(characterId);
    if (controller) {
      // Use 'run' animation (character configs use 'run' not 'walk')
      controller.setState('run', 0.15);
    }
  }
  
  playJumpAnimation(characterId: string): void {
    logger.animation.log(`[ActionHandlers] playJumpAnimation called for ${characterId}`);
    
    if (this.customHandlers.playJumpAnimation) {
      this.customHandlers.playJumpAnimation(characterId);
      return;
    }
    
    const controller = this.animationControllers.get(characterId);
    
    if (controller) {
      logger.animation.log(`[ActionHandlers] Controller found, hasAnimation('jump'): ${controller.hasAnimation('jump')}`);
      
      // Check if jump animation exists
      if (!controller.hasAnimation('jump')) {
        logger.animation.debug(`[ActionHandlers] No jump animation for ${characterId}`);
        // No callback needed - physics system will send PHYSICS_AIRBORNE when falling
        return;
      }
      
      // Play jump as a one-shot animation
      // Don't send completion event - physics system handles PHYSICS_AIRBORNE transition
      controller.playOneShot('jump');
    }
  }
  
  playFallAnimation(characterId: string): void {
    if (this.customHandlers.playFallAnimation) {
      this.customHandlers.playFallAnimation(characterId);
      return;
    }
    
    const controller = this.animationControllers.get(characterId);
    if (controller) {
      controller.setState('fall', 0.2);
    }
  }
  
  playLandAnimation(characterId: string): void {
    if (this.customHandlers.playLandAnimation) {
      this.customHandlers.playLandAnimation(characterId);
      return;
    }
    
    const controller = this.animationControllers.get(characterId);
    const character = this.characters.get(characterId);
    
    if (controller) {
      // Callback that queries CURRENT input state and sends appropriate transition event
      const onLandComplete = () => {
        logger.animation.log(`[ANIM_COMPLETE] Land animation completed for ${characterId}`);
        
        // Query CURRENT input state from controller
        const currentInput = character?.getInputDirection() ?? 0;
        logger.character.log(`[LAND] Checking current input state: ${currentInput}`);
        
        // Send appropriate transition event based on CURRENT input
        if (currentInput !== 0) {
          logger.character.log(`[LAND] Transitioning to walking (input=${currentInput})`);
          StateManager.getInstance().sendCharacterEvent(characterId, { type: 'INPUT_MOVE', direction: currentInput });
        } else {
          logger.character.log(`[LAND] Transitioning to idle (no input)`);
          StateManager.getInstance().sendCharacterEvent(characterId, { type: 'INPUT_STOP' });
        }
      };
      
      // Check if land animation exists - if not, immediately transition out
      if (!controller.hasAnimation('land')) {
        logger.animation.debug(`[ActionHandlers] No land animation for ${characterId}, immediate transition`);
        setTimeout(onLandComplete, 0);
        return;
      }
      
      // Play land as a one-shot animation
      controller.playOneShot('land', onLandComplete);
    }
  }
  
  playTurnAnimation(characterId: string, direction: number): void {
    if (this.customHandlers.playTurnAnimation) {
      this.customHandlers.playTurnAnimation(characterId, direction);
      return;
    }
    
    const controller = this.animationControllers.get(characterId);
    const character = this.characters.get(characterId);
    
    if (controller) {
      // Callback that queries CURRENT input state and sends appropriate transition event
      const onTurnComplete = () => {
        logger.animation.log(`[ANIM_COMPLETE] Turn animation completed for ${characterId}`);
        
        // Query CURRENT input state from controller
        const currentInput = character?.getInputDirection() ?? 0;
        logger.character.log(`[TURN] Checking current input state: ${currentInput}`);
        
        // Send appropriate transition event based on CURRENT input
        if (currentInput !== 0) {
          logger.character.log(`[TURN] Transitioning to walking (input=${currentInput})`);
          StateManager.getInstance().sendCharacterEvent(characterId, { type: 'INPUT_MOVE', direction: currentInput });
        } else {
          logger.character.log(`[TURN] Transitioning to idle (no input)`);
          StateManager.getInstance().sendCharacterEvent(characterId, { type: 'INPUT_STOP' });
        }
      };
      
      // Check if turn animation exists
      if (!controller.hasAnimation('turn')) {
        logger.animation.debug(`[ActionHandlers] No turn animation for ${characterId}, immediate transition`);
        setTimeout(onTurnComplete, 0);
        return;
      }
      
      // Play turn as a one-shot animation (could use direction to pick turn-left vs turn-right)
      controller.playOneShot('turn', onTurnComplete);
    }
  }
  
  // ============================================================================
  // Combat Actions
  // ============================================================================
  
  attachWeapon(characterId: string, attackId: string): void {
    if (this.customHandlers.attachWeapon) {
      this.customHandlers.attachWeapon(characterId, attackId);
      return;
    }
    
    // Default: log, actual weapon attachment is combat-system specific
    logger.core.debug(`[ActionHandlers] attachWeapon: ${characterId}, attack=${attackId}`);
    // CombatSystem handles weapon attachment internally during attack start
  }
  
  detachWeapon(characterId: string): void {
    if (this.customHandlers.detachWeapon) {
      this.customHandlers.detachWeapon(characterId);
      return;
    }
    
    logger.core.debug(`[ActionHandlers] detachWeapon: ${characterId}`);
  }
  
  enableHitbox(characterId: string, attackId: string): void {
    if (this.customHandlers.enableHitbox) {
      this.customHandlers.enableHitbox(characterId, attackId);
      return;
    }
    
    logger.core.debug(`[ActionHandlers] enableHitbox: ${characterId}, attack=${attackId}`);
    // CombatSystem handles hitbox activation during active phase
  }
  
  disableHitbox(characterId: string): void {
    if (this.customHandlers.disableHitbox) {
      this.customHandlers.disableHitbox(characterId);
      return;
    }
    
    logger.core.debug(`[ActionHandlers] disableHitbox: ${characterId}`);
  }
  
  playAttackAnimation(characterId: string, attackId: string, phase: 'windup' | 'active' | 'recovery'): void {
    if (this.customHandlers.playAttackAnimation) {
      this.customHandlers.playAttackAnimation(characterId, attackId, phase);
      return;
    }
    
    logger.core.debug(`[ActionHandlers] playAttackAnimation: ${characterId}, attack=${attackId}, phase=${phase}`);
    // CombatSystem handles animation triggering during attack phases
  }
  
  // ============================================================================
  // Health Actions
  // ============================================================================
  
  triggerDeath(characterId: string): void {
    if (this.customHandlers.triggerDeath) {
      this.customHandlers.triggerDeath(characterId);
      return;
    }
    
    logger.core.info(`[ActionHandlers] triggerDeath: ${characterId}`);
    
    // Stop all movement and disable collision immediately
    const character = this.characters.get(characterId);
    if (character) {
      // Clear any velocity/movement state by sending stop input
      StateManager.getInstance().sendCharacterEvent(characterId, { type: 'INPUT_STOP' });
      
      // Disable collision immediately so dead characters don't block anything
      character.disableCollision();
    }
    
    // Play death animation
    const controller = this.animationControllers.get(characterId);
    if (controller) {
      controller.setState('death', 0.3);
    }
  }
  
  triggerRespawn(characterId: string): void {
    if (this.customHandlers.triggerRespawn) {
      this.customHandlers.triggerRespawn(characterId);
      return;
    }
    
    logger.core.info(`[ActionHandlers] triggerRespawn: ${characterId}`);
  }
  
  // ============================================================================
  // Path Constraint Physics Actions (Phase 3)
  // ============================================================================
  
  onPathConstraintEnabled(characterId: string): void {
    if (this.customHandlers.onPathConstraintEnabled) {
      this.customHandlers.onPathConstraintEnabled(characterId);
      return;
    }
    
    logger.core.debug(`[ActionHandlers] onPathConstraintEnabled: ${characterId}`);
    // Config-driven hook for enabling force-based path constraint
    // Character.updatePhysics will apply spring-damper forces when pathConstraint.enabled is true
  }
  
  onPathConstraintDisabled(characterId: string): void {
    if (this.customHandlers.onPathConstraintDisabled) {
      this.customHandlers.onPathConstraintDisabled(characterId);
      return;
    }
    
    logger.core.debug(`[ActionHandlers] onPathConstraintDisabled: ${characterId}`);
    // Config-driven hook for disabling force-based path constraint
  }
}

// Singleton export
export const ActionHandlerRegistry = new ActionHandlerRegistryImpl();
